import React, { useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';
import { CameraFeaturesTable, deviceCamTierObj, getCameraTierName, CAM_TIERS, subIsMonthly } from './';
import { useDoModal } from '../user/redux/doModal';
import './CameraSubscriptionSelector.less';

function CameraSubscriptionSelector({
  annual: annualProp,
  onAnnualChange,
  onUpgrade,
  user,
  currentDevice
}) {
  const history = useHistory();
  const { doModal } = useDoModal();
  const [internalAnnual, setInternalAnnual] = useState(annualProp !== undefined ? annualProp : true);

  // Use prop if provided, otherwise use internal state
  const annual = annualProp !== undefined ? annualProp : internalAnnual;

  // Derive current tier and period from currentDevice and user
  const currentTier = useMemo(() => {
    if (!currentDevice || !user) return 'standard';
    const tierObj = deviceCamTierObj(user, currentDevice.macAddress);
    if (!tierObj) return 'standard';
    return getCameraTierName(tierObj.tier);
  }, [currentDevice, user]);

  // Get current subscription to check period
  const currentSubscription = useMemo(() => {
    if (!currentDevice || !user) return null;
    const subscriptions = user?.info?.payments?.subscriptions || [];
    return subscriptions.find(sub =>
      sub.macAddress === currentDevice.macAddress &&
      (sub.productName?.includes('tier1') || sub.productName?.includes('tier2'))
    );
  }, [currentDevice, user]);

  // Check if current subscription period matches the selected tab
  const currentIsMonthly = currentSubscription ? subIsMonthly(currentSubscription) : null;
  const periodMatches = currentIsMonthly !== null && ((annual && !currentIsMonthly) || (!annual && currentIsMonthly));

  // Handle annual/monthly toggle
  const handleAnnualChange = (value) => {
    if (onAnnualChange) {
      onAnnualChange(value);
    } else {
      setInternalAnnual(value);
    }
  };

  // Handle upgrade button click
  const handleUpgrade = (tier) => {
    if (onUpgrade) {
      // Use provided callback (for controlled mode)
      onUpgrade(tier);
    } else {
      // Handle navigation internally (for standalone mode)
      const period = annual ? 'yearly' : 'monthly';
      const productName = tier === 'silver'
        ? `awn-camera-tier1-${period}`
        : `awn-camera-tier2-${period}`;
      doModal(null); // Close any open modal before navigating
      const deviceId = currentDevice?._id;
      history.push(`/subscriptions/checkout?subscription=${productName}${deviceId ? `&deviceId=${deviceId}` : ''}`);
    }
  };

  // Determine button state for each tier
  const getSilverButton = () => {
    if (currentTier === 'silver' && periodMatches) {
      return { label: 'Current', disabled: true };
    } else if (currentTier === 'silver' && currentIsMonthly === false && !annual) {
      // Same tier but switching from annual to monthly is a downgrade
      return { label: 'Downgrade', disabled: false };
    } else if (currentTier === 'gold') {
      return { label: 'Downgrade', disabled: false };
    }
    return { label: 'Upgrade', disabled: false };
  };

  const getGoldButton = () => {
    if (currentTier === 'gold' && periodMatches) {
      return { label: 'Current', disabled: true };
    } else if (currentTier === 'gold' && currentIsMonthly === false && !annual) {
      // Same tier but switching from annual to monthly is a downgrade
      return { label: 'Downgrade', disabled: false };
    }
    return { label: 'Upgrade', disabled: false };
  };

  const silverBtn = getSilverButton();
  const goldBtn = getGoldButton();

  // Get pricing from CAM_TIERS
  const silverMonthly = CAM_TIERS.find(tier => tier.productName === 'awn-camera-tier1-monthly');
  const silverAnnual = CAM_TIERS.find(tier => tier.productName === 'awn-camera-tier1-yearly');
  const goldMonthly = CAM_TIERS.find(tier => tier.productName === 'awn-camera-tier2-monthly');
  const goldAnnual = CAM_TIERS.find(tier => tier.productName === 'awn-camera-tier2-yearly');

  // Calculate monthly equivalent for annual plans
  const silverMonthlyPrice = annual
    ? silverAnnual?.price ? `$${(silverAnnual.price / 12).toFixed(2)}` : '$4.16'
    : silverMonthly?.displayPrice?.split('/')[0] || '$4.99';
  const silverAnnualPrice = silverAnnual?.price ? `$${silverAnnual.price}` : '$49.99';

  const goldMonthlyPrice = annual
    ? goldAnnual?.price ? `$${(goldAnnual.price / 12).toFixed(2)}` : '$8.33'
    : goldMonthly?.displayPrice?.split('/')[0] || '$9.99';
  const goldAnnualPrice = goldAnnual?.price ? `$${goldAnnual.price}` : '$99.99';

  return (
    <div className="payment-camera-subscription-selector">
      <div className={classNames('awn-switch two-way switch-wrap global-toggle', annual ? 'annual' : 'monthly')}>
        <div className={classNames('switch-container', annual ? 'annual' : 'monthly')}>
          <span className='slider' />
          <button
            className={classNames('switch-btn', { active: annual })}
            onClick={() => handleAnnualChange(true)}
          >
            Annual
          </button>
          <button
            className={classNames('switch-btn', { active: !annual })}
            onClick={() => handleAnnualChange(false)}
          >
            Monthly
          </button>
        </div>
        <span className='discount'>Save 17%</span>
      </div>

      <div className='product-tiers'>
        <div className={classNames('tier tier-silver', {
          active: currentTier === 'silver',
          annual
        })}>
          <div className='top'>
            <h3>Camera Silver subscription</h3>
            <h2>
              {silverMonthlyPrice}
              <span className='per-month'>/month</span>
            </h2>
            {annual && <p className='billed-annually'>Billed annually at {silverAnnualPrice}</p>}
            <button
              className='btn btn-primary'
              disabled={silverBtn.disabled}
              onClick={() => handleUpgrade('silver')}
            >
              {silverBtn.label}
            </button>
          </div>
          <div className='bottom'>
            Ideal for individuals who want clearer HD time-lapses and reliable day-to-day weather tracking.
          </div>
        </div>

        <div className={classNames('tier tier-gold', {
          active: currentTier === 'gold',
          annual
        })}>
          <div className='top'>
            <h3>Camera Gold subscription</h3>
            <h2>
              {goldMonthlyPrice}
              <span className='per-month'>/month</span>
            </h2>
            {annual && <p className='billed-annually'>Billed annually at {goldAnnualPrice}</p>}
            <button
              className='btn btn-primary'
              disabled={goldBtn.disabled}
              onClick={() => handleUpgrade('gold')}
            >
              {goldBtn.label}
            </button>
          </div>
          <div className='bottom'>
            Best for advanced users needing detailed Full HD visuals and long-term trend insights.
          </div>
        </div>
      </div>

      <div className='features'>
        <h3>Camera Subscription Features</h3>
        <CameraFeaturesTable
          onClick={handleUpgrade}
          currentTier={currentTier}
          annual={annual}
          user={user}
          currentDevice={currentDevice}
        />
      </div>
    </div>
  );
}

CameraSubscriptionSelector.propTypes = {};
CameraSubscriptionSelector.defaultProps = {};
CameraSubscriptionSelector.displayName = 'CameraSubscriptionSelector';

export default CameraSubscriptionSelector;
